import lessonPlanService from "@/services/lessonPlanService";
import professionalDevelopmentService from "@/services/professionalDevelopmentService";
import { SlideDeck } from "@/tiptap-extensions/slide-deck/slideDeckExtension";
import { SlideNotes } from "@/tiptap-extensions/slide-deck/slideExtensions";
import { setupSlideThumbnailHandlers } from "@/tiptap-extensions/slide-deck/slideModalHelper";
import TooltipBlock, {
  TooltipBody,
  TooltipTitle,
} from "@/tiptap-extensions/tooltip/tooltipBlock";
import { CATEGORY_TYPES } from "@/utils/constants";
import { Extension } from "@tiptap/core";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextAlign from "@tiptap/extension-text-align";
import Typography from "@tiptap/extension-typography";
import Underline from "@tiptap/extension-underline";
import Youtube from "@tiptap/extension-youtube";
import StarterKit from "@tiptap/starter-kit";
import { Editor } from "@tiptap/vue-3";
import { Plugin, PluginKey } from "prosemirror-state";
import ImageResize from "tiptap-extension-resize-image";
import { v4 as uuidv4 } from "uuid";
import { computed, nextTick, ref } from "vue";

// Define the list of node types that should have a blockId
const TARGET_NODE_TYPES_FOR_BLOCKID = [
  "paragraph",
  "heading",
  "orderedList",
  "bulletList",
  "blockquote",
  "codeBlock",
  "tooltipBlock",
  "slideDeck",
  "slideNotes",
];

const CellTextAlign = Extension.create({
  name: "cellTextAlign",
  addCommands() {
    return {
      setCellTextAlign:
        (alignment) =>
        ({ editor, chain }) => {
          if (editor.isActive("tableCell")) {
            return chain()
              .updateAttributes("tableCell", {
                style: `text-align: ${alignment}`,
              })
              .run();
          }
          if (editor.isActive("tableHeader")) {
            return chain()
              .updateAttributes("tableHeader", {
                style: `text-align: ${alignment}`,
              })
              .run();
          }
          return false;
        },
    };
  },
});

const BlockId = Extension.create({
  name: "blockId",
  addGlobalAttributes() {
    return [
      {
        types: TARGET_NODE_TYPES_FOR_BLOCKID,
        attributes: {
          blockId: {
            default: null,
            parseHTML: (element) =>
              element.getAttribute("data-block-id") || null,
            renderHTML: (attributes) => {
              if (attributes.blockId) {
                return { "data-block-id": attributes.blockId };
              }
              return {};
            },
          },
        },
      },
    ];
  },
  addProseMirrorPlugins() {
    const P_KEY = new PluginKey("ensureUniqueBlockIds");
    return [
      new Plugin({
        key: P_KEY,
        appendTransaction: (transactions, oldState, newState) => {
          const docChanged = transactions.some((tr) => tr.docChanged);
          if (!docChanged) {
            return null;
          }
          const tr = newState.tr;
          let modifiedInThisRun = false;
          const seenIdsInDoc = new Set();
          const nodesToUpdate = [];
          newState.doc.descendants((node, pos) => {
            if (TARGET_NODE_TYPES_FOR_BLOCKID.includes(node.type.name)) {
              const currentId = node.attrs.blockId;
              if (currentId) {
                if (seenIdsInDoc.has(currentId)) {
                  nodesToUpdate.push({ pos, node, reason: "duplicate_id" });
                } else {
                  seenIdsInDoc.add(currentId);
                }
              } else {
                nodesToUpdate.push({ pos, node, reason: "missing_id" });
              }
            }
          });
          if (nodesToUpdate.length > 0) {
            nodesToUpdate.forEach((item) => {
              let newId = uuidv4();
              while (seenIdsInDoc.has(newId)) {
                newId = uuidv4();
              }
              tr.setNodeMarkup(item.pos, undefined, {
                ...item.node.attrs,
                blockId: newId,
              });
              seenIdsInDoc.add(newId);
              modifiedInThisRun = true;
            });
          }
          return modifiedInThisRun ? tr : null;
        },
      }),
    ];
  },
});

// Simple extension placeholder for consistency (line break preservation is handled in HTML serialization)
const PreserveLineBreaks = Extension.create({
  name: "preserveLineBreaks",
  // This extension serves as a placeholder - actual line break preservation
  // is handled in the onUpdate and emitTabsUpdated functions
});

export function useBaseEditor(config, emit) {
  // Get default tab structure based on config
  const getDefaultTabs = () => {
    // If startEmpty is true, return empty array
    if (config.startEmpty) {
      return [];
    }

    // If custom default tabs are provided, use them
    if (config.customDefaultTabs && Array.isArray(config.customDefaultTabs)) {
      return config.customDefaultTabs.map((tab) => ({
        ...tab,
        sections:
          tab.type === "module"
            ? tab.sections || [
                {
                  editor: null,
                  content: `<p>${config.initialPlaceholder}</p>`,
                },
              ]
            : undefined,
        assessments:
          tab.type === "assessment"
            ? tab.assessments || [
                {
                  title: "Assessment 1",
                  items: [
                    { text: "Assessment item 1" },
                    { text: "Assessment item 2" },
                    { text: "Assessment item 3" },
                  ],
                },
              ]
            : undefined,
      }));
    }

    if (config.defaultTabStructure === "professional-development") {
      return [
        {
          name: "Overview",
          type: "module",
          sections: [
            { editor: null, content: `<p>${config.initialPlaceholder}</p>` },
          ],
          duration: "",
        },
        {
          name: "Objectives",
          type: "module",
          sections: [
            { editor: null, content: `<p>${config.initialPlaceholder}</p>` },
          ],
          duration: "",
        },
        {
          name: "Activities",
          type: "module",
          sections: [
            { editor: null, content: `<p>${config.initialPlaceholder}</p>` },
          ],
          duration: "",
        },
        {
          name: "Resources",
          type: "module",
          sections: [
            { editor: null, content: `<p>${config.initialPlaceholder}</p>` },
          ],
          duration: "",
        },
      ];
    }

    // Default lesson plan structure
    return [
      {
        name: "Introduction",
        type: "module",
        sections: [
          { editor: null, content: `<p>${config.initialPlaceholder}</p>` },
        ],
        duration: "",
      },
      {
        name: "Opening Activity",
        type: "module",
        sections: [
          { editor: null, content: `<p>${config.initialPlaceholder}</p>` },
        ],
        duration: "",
      },
      {
        name: "Main Activity",
        type: "module",
        sections: [
          { editor: null, content: `<p>${config.initialPlaceholder}</p>` },
        ],
        duration: "",
      },
      {
        name: "Closing Activity",
        type: "module",
        sections: [
          { editor: null, content: `<p>${config.initialPlaceholder}</p>` },
        ],
        duration: "",
      },
      ...(config.enableAssessment
        ? [
            {
              name: "Assessment",
              type: "assessment",
              assessments: [
                {
                  title: "Assessment 1",
                  items: [
                    { text: "Assessment item 1" },
                    { text: "Assessment item 2" },
                    { text: "Assessment item 3" },
                  ],
                },
              ],
              duration: "",
            },
          ]
        : []),
    ];
  };

  const tabs = ref(getDefaultTabs());
  const activeTabIndex = ref(0);
  const editingTabIndex = ref(null);
  const tabInputRefs = ref([]);
  const assessmentSectionRef = ref(null);
  const showTabTypeModal = ref(false);

  // Slides functionality
  const showSlideModal = ref(false);
  const slidesAttached = ref(false);
  const attachedSlide = ref(null);
  const isChangingSlides = ref(false);

  // Materials functionality
  const showMaterialsModal = ref(false);
  const editingMaterialIndex = ref(null);
  const editingMaterial = ref(null);
  const instructionalMaterials = ref([]);
  const materialButtons = ref([{ id: 0 }]);

  const hasAssessmentTab = computed(() => {
    return tabs.value.some((tab) => tab.type === "assessment");
  });

  const directSlideModalTabs = computed(() => {
    const moduleTabs = tabs.value.filter((tab) => tab.type === "module");
    return moduleTabs.map((tab, index) => ({
      name: tab.name,
      value: index.toString(),
    }));
  });

  const createEditor = (
    content = `<p>${config.initialPlaceholder}</p>`,
    sectionRef = null
  ) => {
    return new Editor({
      extensions: [
        StarterKit.configure({
          heading: false,
          paragraph: {
            HTMLAttributes: {
              class: "editor-paragraph",
            },
          },
        }),
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
        }),
        Heading.configure({ levels: [1, 2, 3, 4] }),
        TextAlign.configure({
          types: ["heading", "paragraph", "tableCell", "tableHeader"],
        }),
        Highlight.configure({ multicolor: true }),
        Typography,
        Image.configure({
          inline: false,
          allowBase64: true,
          HTMLAttributes: {
            style: null,
            width: null,
          },
        }),
        ImageResize,
        Table.configure({
          resizable: true,
          HTMLAttributes: {
            class: "default-table",
          },
          addAttributes() {
            return {
              ...this.parent?.(),
              class: {
                default: "default-table",
                parseHTML: (element) =>
                  element.getAttribute("class") || "default-table",
                renderHTML: (attributes) => {
                  return {
                    class: attributes.class || "default-table",
                  };
                },
              },
            };
          },
        }),
        TableRow,
        TableHeader.extend({
          addAttributes() {
            return {
              ...this.parent?.(),
              background: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-background"),
                renderHTML: (attributes) => {
                  return {
                    "data-background": attributes.background,
                    style: attributes.background
                      ? `background-color: ${attributes.background}${
                          attributes.style ? "; " + attributes.style : ""
                        }`
                      : attributes.style,
                  };
                },
              },
              style: {
                default: null,
              },
              scope: {
                default: "col",
                parseHTML: (element) => element.getAttribute("scope") || "col",
                renderHTML: (attributes) => ({
                  scope: attributes.scope || "col",
                }),
              },
            };
          },
        }),
        TableCell.extend({
          addAttributes() {
            return {
              ...this.parent?.(),
              background: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-background"),
                renderHTML: (attributes) => {
                  return {
                    "data-background": attributes.background,
                    style: attributes.background
                      ? `background-color: ${attributes.background}${
                          attributes.style ? "; " + attributes.style : ""
                        }`
                      : attributes.style,
                  };
                },
              },
              style: {
                default: null,
              },
            };
          },
        }),
        CellTextAlign,
        Youtube.configure({
          controls: false,
          nocookie: true,
        }),
        TooltipBlock,
        TooltipTitle,
        TooltipBody,
        SlideDeck,
        SlideNotes,
        BlockId,
        PreserveLineBreaks,
      ],
      content: content,
      onUpdate: ({ editor }) => {
        if (sectionRef) {
          let html = editor.getHTML();
          // Apply line break preservation
          html = html.replace(
            /<p([^>]*class="[^"]*editor-paragraph[^"]*"[^>]*)><\/p>/g,
            '<p$1><br class="ProseMirror-trailingBreak"></p>'
          );
          sectionRef.content = html;
          emitTabsUpdated();
        }
      },
      onTransaction: ({ editor, transaction }) => {
        if (transaction.docChanged) {
          setTimeout(() => {
            const dom = editor.view.dom;
            const tables = dom.querySelectorAll(
              "table:not(.default-table):not(.borderless-table)"
            );
            tables.forEach((table) => {
              table.classList.add("default-table");
            });
          }, 0);
        }
      },
    });
  };

  const createEditorWithJSON = (contentJSON, sectionRef = null) => {
    return new Editor({
      extensions: [
        StarterKit.configure({
          heading: false,
          paragraph: {
            HTMLAttributes: {
              class: "editor-paragraph",
            },
          },
        }),
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
        }),
        Heading.configure({ levels: [1, 2, 3, 4] }),
        TextAlign.configure({
          types: ["heading", "paragraph", "tableCell", "tableHeader"],
        }),
        Highlight.configure({ multicolor: true }),
        Typography,
        Image.configure({
          inline: false,
          allowBase64: true,
          HTMLAttributes: {
            style: null,
            width: null,
          },
        }),
        ImageResize,
        Table.configure({
          resizable: true,
          HTMLAttributes: {
            class: "default-table",
          },
          addAttributes() {
            return {
              ...this.parent?.(),
              class: {
                default: "default-table",
                parseHTML: (element) =>
                  element.getAttribute("class") || "default-table",
                renderHTML: (attributes) => {
                  return {
                    class: attributes.class || "default-table",
                  };
                },
              },
            };
          },
        }),
        TableRow,
        TableHeader.extend({
          addAttributes() {
            return {
              ...this.parent?.(),
              background: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-background"),
                renderHTML: (attributes) => {
                  return {
                    "data-background": attributes.background,
                    style: attributes.background
                      ? `background-color: ${attributes.background}${
                          attributes.style ? "; " + attributes.style : ""
                        }`
                      : attributes.style,
                  };
                },
              },
              style: {
                default: null,
              },
              scope: {
                default: "col",
                parseHTML: (element) => element.getAttribute("scope") || "col",
                renderHTML: (attributes) => ({
                  scope: attributes.scope || "col",
                }),
              },
            };
          },
        }),
        TableCell.extend({
          addAttributes() {
            return {
              ...this.parent?.(),
              background: {
                default: null,
                parseHTML: (element) => element.getAttribute("data-background"),
                renderHTML: (attributes) => {
                  return {
                    "data-background": attributes.background,
                    style: attributes.background
                      ? `background-color: ${attributes.background}${
                          attributes.style ? "; " + attributes.style : ""
                        }`
                      : attributes.style,
                  };
                },
              },
              style: {
                default: null,
              },
            };
          },
        }),
        CellTextAlign,
        Youtube.configure({
          controls: false,
          nocookie: true,
        }),
        TooltipBlock,
        TooltipTitle,
        TooltipBody,
        SlideDeck,
        SlideNotes,
        BlockId,
        PreserveLineBreaks,
      ],
      content: contentJSON,
      onUpdate: ({ editor }) => {
        if (sectionRef) {
          let html = editor.getHTML();
          // Apply line break preservation
          html = html.replace(
            /<p([^>]*class="[^"]*editor-paragraph[^"]*"[^>]*)><\/p>/g,
            '<p$1><br class="ProseMirror-trailingBreak"></p>'
          );
          sectionRef.content = html;
          emitTabsUpdated();
        }
      },
      onTransaction: ({ editor, transaction }) => {
        if (transaction.docChanged) {
          setTimeout(() => {
            const dom = editor.view.dom;
            const tables = dom.querySelectorAll(
              "table:not(.default-table):not(.borderless-table)"
            );
            tables.forEach((table) => {
              table.classList.add("default-table");
            });
          }, 0);
        }
      },
    });
  };

  const initializeEditors = () => {
    tabs.value.forEach((tab) => {
      if (tab.type === "module" && tab.sections) {
        tab.sections.forEach((section) => {
          if (!section.editor) {
            section.editor = createEditor(section.content, section);
          }
        });
      }
    });
  };

  const destroyEditor = (editorInstance) => {
    if (editorInstance && !editorInstance.isDestroyed) {
      editorInstance.destroy();
    }
  };

  const switchTab = (index) => {
    if (editingTabIndex.value === index) return;
    if (editingTabIndex.value !== null) {
      finishEditingTab();
    }
    activeTabIndex.value = index;
  };

  const handleAddTab = () => {
    if (config.enableAssessment && hasAssessmentTab.value) {
      addNewTab("module");
    } else {
      showTabTypeModal.value = true;
    }
  };

  const addNewTab = (type) => {
    const newTabIndex = tabs.value.length;

    if (type === "module") {
      const moduleCount = tabs.value.filter(
        (tab) => tab.type === "module"
      ).length;

      tabs.value.push({
        name: `${config.moduleTabLabel || "Module"} ${moduleCount + 1}`,
        type: "module",
        sections: [
          { editor: null, content: `<p>${config.initialPlaceholder}</p>` },
        ],
        duration: "",
      });

      nextTick(() => {
        tabs.value[newTabIndex].sections[0].editor = createEditor(
          tabs.value[newTabIndex].sections[0].content,
          tabs.value[newTabIndex].sections[0]
        );
        activeTabIndex.value = newTabIndex;
      });
    } else if (
      type === "assessment" &&
      config.enableAssessment &&
      !hasAssessmentTab.value
    ) {
      tabs.value.push({
        name: `Assessment`,
        type: "assessment",
        assessments: [
          {
            title: "Assessment 1",
            items: [
              { text: "Assessment item 1" },
              { text: "Assessment item 2" },
              { text: "Assessment item 3" },
            ],
          },
        ],
        duration: "",
      });

      activeTabIndex.value = newTabIndex;
    }

    tabInputRefs.value.length = tabs.value.length;
    showTabTypeModal.value = false;
    emitTabsUpdated();
  };

  const removeTab = (index) => {
    if (index < 0 || index >= tabs.value.length) return;

    if (tabs.value[index].type === "module") {
      tabs.value[index].sections.forEach((section) => {
        destroyEditor(section.editor);
      });
    }

    tabs.value.splice(index, 1);
    tabInputRefs.value.splice(index, 1);

    if (tabs.value.length === 0) {
      activeTabIndex.value = -1;
    } else if (activeTabIndex.value >= index) {
      activeTabIndex.value = Math.max(0, activeTabIndex.value - 1);
    }

    emitTabsUpdated();
  };

  const startEditingTab = (index) => {
    if (editingTabIndex.value !== null && editingTabIndex.value !== index) {
      finishEditingTab();
    }
    editingTabIndex.value = index;
    nextTick(() => {
      const inputEl = tabInputRefs.value[index];
      if (inputEl && typeof inputEl.focus === "function") {
        inputEl.focus();
        inputEl.select();
      }
    });
  };

  const finishEditingTab = () => {
    if (editingTabIndex.value !== null && tabs.value[editingTabIndex.value]) {
      if (!tabs.value[editingTabIndex.value].name.trim()) {
        tabs.value[editingTabIndex.value].name = `Section ${
          editingTabIndex.value + 1
        }`;
      }
    }
    editingTabIndex.value = null;
    emitTabsUpdated();
  };

  const addNestedSection = (tabIndex) => {
    if (
      tabIndex < 0 ||
      tabIndex >= tabs.value.length ||
      tabs.value[tabIndex].type !== "module"
    )
      return;

    const newSection = {
      editor: null,
      content: `<p>${config.initialPlaceholder}</p>`,
    };
    tabs.value[tabIndex].sections.push(newSection);

    nextTick(() => {
      const newSectionIndex = tabs.value[tabIndex].sections.length - 1;
      tabs.value[tabIndex].sections[newSectionIndex].editor = createEditor(
        newSection.content,
        tabs.value[tabIndex].sections[newSectionIndex]
      );
    });

    emitTabsUpdated();
  };

  const removeSection = (tabIndex, sectionIndex) => {
    if (
      tabIndex < 0 ||
      tabIndex >= tabs.value.length ||
      tabs.value[tabIndex].type !== "module" ||
      sectionIndex <= 0 ||
      sectionIndex >= tabs.value[tabIndex].sections.length
    )
      return;

    const sectionToRemove = tabs.value[tabIndex].sections[sectionIndex];
    destroyEditor(sectionToRemove.editor);
    tabs.value[tabIndex].sections.splice(sectionIndex, 1);

    emitTabsUpdated();
  };

  const updateAssessments = (newAssessments) => {
    if (
      activeTabIndex.value >= 0 &&
      tabs.value[activeTabIndex.value].type === "assessment"
    ) {
      tabs.value[activeTabIndex.value].assessments = newAssessments;
      emitTabsUpdated();
    }
  };

  const formatDurationInput = (event, tabIndex) => {
    const numericValue = event.target.value.replace(/[^0-9]/g, "");
    tabs.value[tabIndex].duration = numericValue;
    nextTick(() => {
      event.target.value = numericValue;
    });
    emitTabsUpdated();
  };

  const deleteMaterial = async (id) => {
    if (config.moduleType === CATEGORY_TYPES.LessonPlan) {
      await lessonPlanService.deleteMaterials(id);
    } else if (config.moduleType === CATEGORY_TYPES.ProfessionalDevelopment) {
      await professionalDevelopmentService.deleteMaterials(id);
    }
  };

  const emitTabsUpdated = () => {
    const serializedTabs = tabs.value.map((tab) => {
      if (tab.type === "module") {
        return {
          name: tab.name,
          type: tab.type,
          duration: tab.duration,
          sections: tab.sections.map((section) => {
            let content = section.editor
              ? section.editor.getHTML()
              : section.content;

            // Apply line break preservation to HTML content
            if (section.editor && content) {
              content = content.replace(
                /<p([^>]*class="[^"]*editor-paragraph[^"]*"[^>]*)><\/p>/g,
                '<p$1><br class="ProseMirror-trailingBreak"></p>'
              );
            }

            const json = section.editor
              ? JSON.stringify(section.editor.getJSON())
              : null;

            return {
              content,
              json,
            };
          }),
        };
      } else {
        return {
          name: tab.name,
          type: tab.type,
          duration: tab.duration,
          assessments: tab.assessments,
        };
      }
    });

    emit("tabs-updated", serializedTabs, instructionalMaterials.value);

    nextTick(() => {
      setupSlideThumbnailHandlers();
    });
  };

  const updateFromParent = (newTabs, newMaterials = []) => {
    if (!newTabs || newTabs.length === 0) return;

    tabs.value.forEach((tab) => {
      if (tab.type === "module" && tab.sections) {
        tab.sections.forEach((section) => {
          destroyEditor(section.editor);
        });
      }
    });

    if (newTabs && Array.isArray(newTabs)) {
      tabs.value = newTabs.map((tab) => {
        if (tab.type === "module") {
          return {
            name: tab.name,
            type: tab.type || "module",
            duration: tab.duration,
            sections: (tab.sections || []).map((section) => {
              return {
                editor: null,
                content: section.content,
                json: section.json || null,
              };
            }),
          };
        } else {
          return {
            name: tab.name,
            type: "assessment",
            duration: tab.duration,
            assessments: tab.assessments || [],
          };
        }
      });
    }

    if (Array.isArray(newMaterials)) {
      instructionalMaterials.value = [...newMaterials];
      materialButtons.value = [{ id: 0 }];
      for (let i = 1; i < instructionalMaterials.value.length; i++) {
        if (instructionalMaterials.value[i]) {
          materialButtons.value.push({ id: i });
        }
      }
    }

    nextTick(() => {
      tabs.value.forEach((tab) => {
        if (tab.type === "module" && tab.sections) {
          tab.sections.forEach((section) => {
            if (section.json) {
              try {
                const contentJson = JSON.parse(section.json);
                section.editor = createEditorWithJSON(contentJson, section);
              } catch (e) {
                console.error("Error parsing JSON content:", e);
                section.editor = createEditor(section.content, section);
              }
            } else {
              section.editor = createEditor(section.content, section);
            }
          });
        }
      });
    });
  };

  // Slide functionality
  const openSlideModal = () => {
    nextTick(() => {
      showSlideModal.value = true;
    });
  };

  const closeSlideModal = () => {
    showSlideModal.value = false;
  };

  const handleSlideInsert = (slideData) => {
    const formattedContent = {
      type: slideData.type,
      data: slideData,
    };
    insertContent(formattedContent);

    slidesAttached.value = true;
    attachedSlide.value = {
      url: slideData.url,
      code: slideData.slideUploadCode,
    };

    isChangingSlides.value = false;
    closeSlideModal();
  };

  const insertContent = (content) => {
    if (activeTabIndex.value < 0 || tabs.value.length === 0) {
      alert("Please select a module tab before inserting content");
      return;
    }

    if (!content.data) {
      alert("No content to insert");
      return;
    }

    const currentTab = tabs.value[activeTabIndex.value];

    if (currentTab.type !== "module") {
      alert("Content can only be inserted into module tabs");
      return;
    }

    if (content.type === "slideDeck") {
      const { slideSelectionsByTab } = content.data;

      Object.entries(slideSelectionsByTab).forEach(([tabValue, slidesList]) => {
        if (!slidesList || slidesList.length === 0) return;

        const tabIndex = parseInt(tabValue);

        if (
          isNaN(tabIndex) ||
          tabIndex >= tabs.value.length ||
          tabs.value[tabIndex].type !== "module"
        ) {
          console.warn(`Invalid tab index: ${tabValue}, skipping these slides`);
          return;
        }

        const targetTab = tabs.value[tabIndex];

        if (targetTab.sections.length === 0) {
          console.warn(
            `Tab ${tabIndex} has no sections, skipping these slides`
          );
          return;
        }

        const editor = targetTab.sections[0].editor;
        if (!editor) return;

        const slidesToInsert = [];

        slidesToInsert.push({
          type: "paragraph",
          content: [],
        });

        slidesList.forEach((slide) => {
          slidesToInsert.push({
            type: "slideDeck",
            attrs: {
              thumbnailUrl: slide.thumbnailUrl,
              slideIndex: slide.index,
              title: `Slide ${slide.index + 1}`,
              notes: slide.notes || "",
            },
            content: [
              {
                type: "slideNotes",
                content: slide.notes
                  ? [
                      {
                        type: "paragraph",
                        content: [{ type: "text", text: slide.notes }],
                      },
                    ]
                  : [{ type: "paragraph" }],
              },
            ],
          });

          slidesToInsert.push({
            type: "paragraph",
            content: [],
          });
        });

        if (slidesToInsert.length > 0) {
          const currentPos = editor.state.doc.content.size;

          editor
            .chain()
            .focus()
            .insertContentAt(currentPos, slidesToInsert)
            .run();
        }
      });

      emitTabsUpdated();
    }
  };

  const setSlideAttachedState = (slideData) => {
    slidesAttached.value = true;
    attachedSlide.value = slideData;
  };

  // Materials functionality
  const hasMaterialAt = (index) => {
    return instructionalMaterials.value[index] !== undefined;
  };

  const getMaterialUrl = (index) => {
    if (hasMaterialAt(index)) {
      return instructionalMaterials.value[index].url;
    }
    return "#";
  };

  const getMaterialTitle = (index) => {
    if (hasMaterialAt(index)) {
      return instructionalMaterials.value[index].title;
    }
    return "";
  };

  const addNewMaterialButton = () => {
    const nextIndex = materialButtons.value.length;
    materialButtons.value.push({ id: nextIndex });
  };

  const removeMaterialOnly = async (index) => {
    const material = instructionalMaterials.value[index];
    // Access isEditForm from the config or pass it as a parameter
    if (material?.id) {
      await deleteMaterial(material.id);
    }

    instructionalMaterials.value[index] = undefined;
    emitTabsUpdated();
  };

  const removeMaterialButton = async (index) => {
    if (index === 0) return;

    if (hasMaterialAt(index)) {
      const material = instructionalMaterials.value[index];
      if (material?.id) {
        await deleteMaterial(material.id);
      }

      instructionalMaterials.value.splice(index, 1);
    }

    materialButtons.value.splice(index, 1);
    emitTabsUpdated();
  };

  const openMaterialsModal = (index) => {
    editingMaterialIndex.value = index;
    editingMaterial.value =
      index < instructionalMaterials.value.length
        ? { ...instructionalMaterials.value[index] }
        : null;

    showMaterialsModal.value = true;
  };

  const closeMaterialsModal = () => {
    showMaterialsModal.value = false;
    editingMaterialIndex.value = null;
    editingMaterial.value = null;
  };

  const addMaterial = (material) => {
    const index =
      editingMaterialIndex.value !== null
        ? editingMaterialIndex.value
        : instructionalMaterials.value.length;

    while (instructionalMaterials.value.length < index) {
      instructionalMaterials.value.push(undefined);
    }

    instructionalMaterials.value[index] = material;
    emitTabsUpdated();
    closeMaterialsModal();
  };

  const updateMaterial = (index, updatedMaterial) => {
    if (index >= 0 && index < instructionalMaterials.value.length) {
      instructionalMaterials.value[index] = updatedMaterial;
      emitTabsUpdated();
    }
    closeMaterialsModal();
  };

  const setInstructionalMaterials = (materials) => {
    instructionalMaterials.value = materials || [];
    materialButtons.value = [{ id: 0 }];
    for (let i = 1; i < instructionalMaterials.value.length; i++) {
      if (instructionalMaterials.value[i]) {
        materialButtons.value.push({ id: i });
      }
    }
  };

  return {
    // State
    tabs,
    activeTabIndex,
    editingTabIndex,
    tabInputRefs,
    assessmentSectionRef,
    showTabTypeModal,
    hasAssessmentTab,
    directSlideModalTabs,

    // Slides
    showSlideModal,
    slidesAttached,
    attachedSlide,
    isChangingSlides,

    // Materials
    showMaterialsModal,
    editingMaterialIndex,
    editingMaterial,
    instructionalMaterials,
    materialButtons,

    // Methods
    createEditor,
    createEditorWithJSON,
    initializeEditors,
    destroyEditor,
    switchTab,
    handleAddTab,
    addNewTab,
    removeTab,
    startEditingTab,
    finishEditingTab,
    addNestedSection,
    removeSection,
    updateAssessments,
    formatDurationInput,
    emitTabsUpdated,
    updateFromParent,

    // Slides methods
    openSlideModal,
    closeSlideModal,
    handleSlideInsert,
    insertContent,
    setSlideAttachedState,
    isSlideAttached: () => slidesAttached.value,
    getSlideData: () => attachedSlide.value,

    // Materials methods
    hasMaterialAt,
    getMaterialUrl,
    getMaterialTitle,
    addNewMaterialButton,
    removeMaterialOnly,
    removeMaterialButton,
    openMaterialsModal,
    closeMaterialsModal,
    addMaterial,
    updateMaterial,
    setInstructionalMaterials,
    getInstructionalMaterials: () => instructionalMaterials.value,
  };
}
